import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "./icons";
import COLORS from "./colors";

export const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

export const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return d; }
};

// Fuseaux horaires proposés pour les RDV visio (heure de paroi, sans conversion).
export const TIMEZONES = [
  { value: "Asia/Dubai", label: "Dubaï (GST, UTC+4)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/London", label: "Londres (GMT/BST)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "UTC", label: "UTC" },
];
export const tzLabel = (v) => (TIMEZONES.find((t) => t.value === v) || {}).label || v || "";

// Cinq intentions, une couleur chacune (décision de Mélilia du 29/09, lot 7 — rôles des jetons dans src/lib/colors.js) :
//   inerte   : rien n'est attendu de personne — à venir, brouillon, annulé, en pause, non émise
//   en_cours : le travail avance — en cours, émise, programmé, ouvert
//   attente  : quelqu'un doit agir — attente du client / de l'agence, prête à valider, à valider, à payer, partiellement payé
//   echec    : quelque chose revient ou bloque — correction demandée, retard, bloquée
//   acquis   : c'est fait — validée, terminée, payé, publié, résolu (bleu azur : le bordeaux reste l'accent de la marque)
// `cls` habille le badge (bordure comprise), `color` colore l'icône ou le rang. Toute carte de statut passe par S().
export const INTENT = {
  inerte: { cls: "border bg-sable text-gris-texte border-marine/30", color: COLORS.muet },
  en_cours: { cls: "border bg-marine-50 text-marine border-marine/40", color: COLORS.marine },
  attente: { cls: "border bg-ambre-50 text-ambre-texte border-ambre/40", color: COLORS.ambre },
  echec: { cls: "border bg-rouge-50 text-rouge border-rouge/40", color: COLORS.rouge },
  acquis: { cls: "border bg-bleu-50 text-bleu border-bleu/40", color: COLORS.bleu },
};
const S = (label, intent, extra = {}) => ({ label, intent, ...INTENT[intent], ...extra });

export const ETAPE_STATUS = {
  non_demarre: S("À venir", "inerte", { icon: "Circle" }),
  en_cours: S("En cours", "en_cours", { icon: "CircleDot" }),
  en_attente_client: S("En attente de validation", "attente", { icon: "Clock" }),
  en_correction: S("En cours de correction", "echec", { icon: "RefreshCw" }),
  en_attente_agence: S("En attente de validation agence", "attente", { icon: "Clock" }),
  valide: S("Validée", "acquis", { icon: "CheckCircle2" }),  // « Validée » : vocabulaire figé (CLAUDE.md §6), aligné le 28/09
  // « livre » retiré le 28/09 (lot 4, Q5) : six statuts, comme le serveur.
};

// Lot 4 (28/09) : qui a validé l'étape. `validated_by_type` (« entreprise » / « agence ») était écrit
// depuis le 12/08 et jamais restitué hors du menu admin. viewer = "client" : l'entreprise lit « votre
// entreprise » ; les autres rôles lisent « l'entreprise ». Chaîne vide si l'information manque.
export const validatedByLabel = (et, viewer) => {
  if (et?.validated_by_type === "entreprise") return viewer === "client" ? "par votre entreprise" : "par l'entreprise";
  if (et?.validated_by_type === "agence") return "par Metara";
  return "";
};

export const EtapeIcon = ({ status, size = 16 }) => {  const s = ETAPE_STATUS[status];
  if (!s) return null;
  const Ic = ICONS_BY_NAME[s.icon] || Icons.Circle;
  return <Ic size={size} style={{ color: s.color }} strokeWidth={2} />;
};

// Même rôle qu'EtapeIcon pour les phases de MISSION (lot 5, 28/09) — la définition est plus bas dans le
// fichier (MISSION_PHASE_STATES), résolue à l'appel.
export const MissionPhaseIcon = ({ status, size = 16 }) => {
  const s = MISSION_PHASE_STATES[status];
  if (!s) return null;
  const Ic = ICONS_BY_NAME[s.icon] || Icons.Circle;
  return <Ic size={size} style={{ color: s.color }} strokeWidth={2} />;
};

// Statuts de phase (système PROJET, cf. CLAUDE.md §5) — clé technique `cloturee`,
// libellé affiché « Terminée » (décision du 14/08, ne pas « corriger »). Pas de
// `cls` : l'harmonisation chromatique projet/mission est reportée à la refonte
// visuelle globale (Journal Horizon) — un badge texte suffit ici, comme le
// <select> de l'admin (Lot 5) qui n'en portait déjà aucun.
export const PHASE_STATUS = {
  a_venir: { label: "À venir" },
  en_cours: { label: "En cours" },
  cloturee: { label: "Terminée" },
};

// Aplatissement + regroupement étape→phase, écrits séparément cinq et deux fois
// respectivement (ProjectCard.js, AdminClientDetail.js, CollabClientDetail.js,
// ClientProjects.js, ClientProjectDetail.js) — extraits ici à l'audit de clôture
// du chantier Phase (14/08).
export const flattenEtapes = (p) => (p.phases || []).flatMap((ph) => ph.etapes || []);

export const phaseByEtape = (p) => {
  const map = {};
  (p.phases || []).forEach((ph) => (ph.etapes || []).forEach((e) => { map[e.key] = ph; }));
  return map;
};

export const TICKET_STATUS = {
  ouvert: S("Ouvert", "en_cours"),
  en_cours: S("En cours", "en_cours"),
  resolu: S("Résolu", "acquis"),
};

export const POST_STATUS = {
  a_valider: S("À valider", "attente"),
  valide: S("Validé", "acquis"),
  a_corriger: S("À corriger", "echec"),
  programme: S("Programmé", "en_cours"),
  publie: S("Publié", "acquis"),
};

export const POST_PLATFORMS = {
  Instagram: { label: "Instagram", icon: "Instagram" },
  Facebook: { label: "Facebook", icon: "Facebook" },
  LinkedIn: { label: "LinkedIn", icon: "Linkedin" },
  TikTok: { label: "TikTok", icon: "Music2" },
  YouTube: { label: "YouTube", icon: "Youtube" },
  "Google Business": { label: "Fiche Google Business", icon: "MapPin" },
  Autre: { label: "Autre", icon: "Share2" },
};

// Nombre de CONTENUS à produire par mois, déduit du contrat social media. Miroir exact de
// domain.social_contents_per_month côté serveur : les deux doivent donner le même résultat.
// Avec « les mêmes publications sur tous les réseaux », un contenu part partout — 12 + 12 font
// 12 contenus ; sans elle, ils sont distincts et font 24.
export const socialContentsPerMonth = (contrat) => {
  const vols = Object.values((contrat || {}).volumes || {}).map(Number).filter((v) => v > 0);
  if (!vols.length) return 0;
  return (contrat || {}).same_content ? Math.max(...vols) : vols.reduce((a, b) => a + b, 0);
};

export const POST_FORMATS = {
  post: { label: "Post" },
  carrousel: { label: "Carrousel" },
  reel: { label: "Reel" },
  story: { label: "Story" },
  autre: { label: "Autre" },
};

export const MISSION_STATUS = {
  a_venir: S("À venir", "inerte"),
  en_cours: S("En cours", "en_cours"),
  en_attente_validation: S("En attente de validation", "attente"),
  // « en_validation » et « validee » retirés le 28/09 (lot 5) : jamais produits par compute_mission_status
  // ni forcés par l'admin (status_override : en_pause / annulee), donc jamais affichés.
  bloquee: S("Bloquée", "echec"),
  terminee: S("Terminée", "acquis"),
  en_pause: S("En pause", "inerte"),
  annulee: S("Annulée", "inerte"),
  a_corriger: S("À corriger", "echec"),
};

export const PAYMENT_STATUS = {
  a_venir: S("À venir", "inerte"),
  en_attente: S("En attente de paiement", "attente"),
  partiellement_paye: S("Partiellement payé", "attente"),
  paye: S("Payé", "acquis"),
  en_retard: S("En retard", "echec"),
  annule: S("Annulé", "inerte"),
};

export const INVOICE_STATUS = {
  non_emise: S("Non émise", "inerte"),
  emise: S("Émise", "en_cours"),
  en_attente: S("En attente", "attente"),
  paye: S("Payée", "acquis"),
};

export const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", AED: "AED" };
export const money = (n, cur = "EUR") =>
  (Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + "\u00a0" + (CURRENCY_SYMBOLS[cur] || cur || "€"); // insécable : « 4 500 € » ne se coupe plus avant le symbole (30/09)
// eur() garde son nom historique (des dizaines d'appels supposent un montant déjà en EUR,
// ex. les totaux agrégés admin) mais accepte désormais une devise explicite quand elle est connue.
export const eur = (n, cur = "EUR") => money(n, cur);

export const COLLAB_PAY_STATUS = {
  a_venir: S("À venir", "inerte"),
  a_payer: S("À payer", "attente"),
  partiellement_paye: S("Partiellement payé", "attente"),
  paye: S("Payé", "acquis"),
  en_retard: S("En retard", "echec"),
  annule: S("Annulé", "inerte"),
};

export const MISSION_WORKFLOW = [
  { key: "echanges_prep", label: "Échanges préparatoires" },
  { key: "brief", label: "Brief" },
  { key: "captation", label: "Captation" },
  { key: "montage", label: "Montage" },
  { key: "seo", label: "Optimisation SEO" },
  { key: "creation_contenu", label: "Création de contenu" },
  { key: "attente_contenu", label: "En attente de contenu" },
  { key: "validation_projet", label: "Validation du projet" },
];
export const workflowLabel = (k) => (MISSION_WORKFLOW.find((w) => w.key === k) || {}).label || "—";

// Miroir de domain.CLIENT_NATURES côté serveur : les deux listes doivent coïncider, sinon l'écran
// propose une valeur que le serveur refuse. `KOL` en est sorti le 22/10 — c'est un rôle, pas un
// métier, et un rôle se cumule. Le motif complet est dans le commentaire de domain.py.
export const CLIENT_NATURES = [
  "Dentiste libéral",
  "Cabinet dentaire",
  "Regroupement de centres dentaires",
  "Prothésiste dentaire",
  "Laboratoire de prothèse dentaire",
  "Industriel / Distributeur",
  "Organisme de formation",
  "Plateforme",
  "Association",
];

// Les quatre champs du registre des entités (23/10) — miroirs de domain.py, les listes doivent
// coïncider. La chaîne vide reste permise partout et veut dire « pas encore établi » : c'est
// pourquoi aucune liste ne porte de valeur « Non renseigné ».

// À qui le client parle. Champ À PART de l'archétype depuis le 22/10 : deux cabinets sur quatre du
// portefeuille visent leurs pairs, et la déontologie ordinale n'encadre que la parole au public.
export const CLIENT_REGIMES = ["Vers les patients", "Interprofessionnel", "Les deux"];

// Ce que le client est EN PLUS de son métier. Cumulables — un client du portefeuille en porte
// quatre. C'est ce champ qui a remplacé « KOL » dans les archétypes.
export const CLIENT_ROLES = [
  "KOL",
  "Conférencier",
  "Formateur",
  "Membre société savante",
  "Revendeur officiel",
  "Partenaire de formation",
  "Responsable pédagogique",
  "Adhérent mouvement",
];

// Où en est la relation. `client_since` dit depuis quand elle a commencé, pas si elle continue.
export const CLIENT_RELATION_STATUSES = ["Actif", "Actif — pas encore ouvert", "Ancien", "Prospect"];

// Lot 5 (28/09) : six états de phase de mission, plus de pointeur « phase active » — une phase est
// « ouverte » (le collaborateur peut y agir) quand elle n'est ni à venir ni validée.
export const MISSION_OPEN_STATES = ["en_cours", "pret_a_valider", "a_corriger", "bloquee"];
// Mêmes intentions que les étapes de projet (grammaire Projet = référence) : une attente est ambre, une correction
// ou un blocage est rouge, l'acquis est bleu. « Bloquée » n'a pas d'équivalent projet : intention « échec »,
// icône distincte. Mêmes champs qu'ETAPE_STATUS : icon et color pour les rangs.
export const MISSION_PHASE_STATES = {
  a_venir: S("À venir", "inerte", { icon: "Circle" }),
  en_cours: S("En cours", "en_cours", { icon: "CircleDot" }),
  pret_a_valider: S("Prête à valider", "attente", { icon: "Clock" }),
  a_corriger: S("À corriger", "echec", { icon: "RefreshCw" }),
  valide: S("Validée", "acquis", { icon: "CheckCircle2" }),
  bloquee: S("Bloquée", "echec", { icon: "PauseCircle" }),
};

export const fmtPhaseDate = (v) => {
  if (!v) return null;
  const s = String(v);
  const parts = s.split("T")[0].split("-");
  const y = parts[0], m = parts[1], d = parts[2];
  const months = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const mi = parseInt(m, 10) - 1;
  if (isNaN(mi) || !months[mi]) return s;
  return d ? `${parseInt(d, 10)} ${months[mi]} ${y}` : `${months[mi]} ${y}`;
};

export const MODULE_META = {
  projets: { label: "Mes Projets", desc: "Suivez l'avancement de vos projets", icon: "FolderKanban" },
  demandes: { label: "Demandes & Messages", desc: "Échangez avec l'équipe Metara", icon: "MessageSquare" },
  identite: { label: "Identité Visuelle", desc: "Votre kit de marque", icon: "Palette" },
  calendrier: { label: "Social Media", desc: "Validez vos publications", icon: "CalendarDays" },
  aide: { label: "Aide", desc: "Questions fréquentes (FAQ)", icon: "HelpCircle" },
};

// Modules-prestations activables par entreprise (= types de projet)
// Ce qu'une marque fabrique ou propose : la « nature » que porte un rôle (décision du 27/10).
// Miroir exact de domain.ENTITY_DOMAINS — les deux listes doivent coïncider.
export const ENTITY_DOMAINS = ["Implants", "Logiciel", "Scanner / empreinte", "Usinage / impression 3D",
  "Matériaux", "Équipement", "Distribution", "Formation", "Société savante / congrès", "Autre"];

export const SERVICE_MODULES = {
  identite_visuelle: { label: "Identité Visuelle", icon: "Palette" },
  site_web: { label: "Site Internet", icon: "Globe" },
  reseaux_sociaux: { label: "Social Media", icon: "CalendarDays" },
  supports_marketing: { label: "Supports de communication", icon: "Megaphone" },
  videos_motion: { label: "Vidéos & Motion", icon: "Video" },
  evenements: { label: "Événements", icon: "PartyPopper" },
  crm: { label: "CRM", icon: "Database" },
};

export const Badge = ({ status, map }) => {
  const s = (map || ETAPE_STATUS)[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};
